import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Camera, X, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProfilePhotoUploader = ({ 
  currentPhoto, 
  onPhotoChange, 
  size = 'md',
  editable = true 
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24',
    lg: 'w-32 h-32',
    xl: 'w-40 h-40'
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Per favore seleziona un\'immagine');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'immagine deve essere inferiore a 5MB');
      return;
    }

    setUploading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        // Upload to backend
        const token = localStorage.getItem('token');
        await axios.post(`${API}/profile/photo`, {
          photo_base64: base64
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Update local state
        if (onPhotoChange) {
          onPhotoChange(base64);
        }

        // Update user in localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          user.profile_photo = base64;
          localStorage.setItem('user', JSON.stringify(user));
        }

        toast.success('Foto profilo aggiornata!');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Errore nel caricamento della foto');
      setUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/photo`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (onPhotoChange) {
        onPhotoChange(null);
      }

      // Update localStorage
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);
        delete user.profile_photo;
        localStorage.setItem('user', JSON.stringify(user));
      }

      toast.success('Foto profilo rimossa');
    } catch (error) {
      console.error('Remove error:', error);
      toast.error('Errore nella rimozione della foto');
    }
  };

  return (
    <div className="relative inline-block">
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFileSelect}
      />
      
      {/* Photo Circle */}
      <div 
        className={`${sizeClasses[size]} rounded-full overflow-hidden border-4 border-white shadow-lg relative`}
        style={{ background: '#E8F4FD' }}
      >
        {currentPhoto ? (
          <img 
            src={currentPhoto} 
            alt="Foto Profilo" 
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="w-1/2 h-1/2" style={{ color: '#9CA3AF' }} />
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
      </div>

      {/* Edit Button */}
      {editable && !uploading && (
        <button
          onClick={() => fileInputRef.current?.click()}
          className="absolute bottom-0 right-0 p-2 rounded-full shadow-lg transition-transform hover:scale-110"
          style={{ background: '#2A9FCB', color: 'white' }}
          title="Cambia foto"
          data-testid="change-photo-btn"
        >
          <Camera className="w-4 h-4" />
        </button>
      )}

      {/* Remove Button */}
      {editable && currentPhoto && !uploading && (
        <button
          onClick={handleRemovePhoto}
          className="absolute top-0 right-0 p-1 rounded-full bg-red-500 text-white shadow-lg transition-transform hover:scale-110"
          title="Rimuovi foto"
          data-testid="remove-photo-btn"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
};

export default ProfilePhotoUploader;
